import type { Phoneme } from "@/generated/graphql";
import { useEffect, useRef, useState } from "react";
import { FaMusic } from "react-icons/fa";
import { HiDotsVertical, HiPencil, HiTrash } from "react-icons/hi";

interface PhonemeCardProps {
  phoneme: Phoneme;
  onClick: () => void;
  disabled: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
}

export function PhonemeCard({ phoneme, onClick, disabled, onEdit, onDelete }: PhonemeCardProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuOpen]);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className={`
          w-full p-3 border rounded-lg transition-all
          ${
            disabled
              ? "opacity-50 cursor-not-allowed bg-slate-50"
              : "hover:shadow-md hover:border-primary-300 bg-white cursor-pointer"
          }
        `}
      >
        <div className="text-3xl font-bold text-primary-600 mb-2">{phoneme.phoneme}</div>

        {phoneme.tags.length > 0 && (
          <div className="justify-center flex flex-wrap gap-1 mb-2 min-h-[20px]">
            {phoneme.tags.slice(0, 3).map((tag) => (
              <span
                key={tag}
                className="inline-block px-2 py-0.5 text-[10px] font-medium bg-slate-100 text-slate-600 rounded"
              >
                {tag}
              </span>
            ))}
            {phoneme.tags.length > 3 && (
              <span className="inline-block px-2 py-0.5 text-[10px] font-medium text-slate-400">
                +{phoneme.tags.length - 3}
              </span>
            )}
          </div>
        )}

        <div className="flex items-center justify-center text-xs text-slate-400">
          <FaMusic className="mr-1" />
          <span>
            {phoneme.audioCount} {phoneme.audioCount === 1 ? "voice" : "voices"}
          </span>
        </div>
      </button>

      {/* Three-dot admin menu */}
      {(onEdit || onDelete) && (
        <div ref={menuRef} className="absolute top-1.5 right-1.5">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setMenuOpen((v) => !v);
            }}
            className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition-colors"
          >
            <HiDotsVertical size={14} />
          </button>

          {menuOpen && (
            <div className="absolute right-0 top-6 z-50 w-32 bg-white border border-slate-200 rounded-lg shadow-lg py-1">
              {onEdit && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setMenuOpen(false);
                    onEdit();
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                >
                  <HiPencil size={13} />
                  Edit
                </button>
              )}
              {onDelete && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setMenuOpen(false);
                    onDelete();
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                >
                  <HiTrash size={13} />
                  Delete
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
